import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useContext,
} from "react";
import { useCustomNavigate as useNavigate } from "#hooks";
import { useTranslation } from "react-i18next";

import {
  Answer,
  Block,
  Grid,
  GridItem,
  Loading,
  Dropdown,
  TabsUnderlined,
  Tabs,
  NewButton,
  Icon,
  NotFoundCard,
} from "@USupport-components-library/src";

import { ThemeContext } from "@USupport-components-library/utils";

import { useGetLanguages, useGetQuestionsTags } from "#hooks";

import "./my-qa.scss";

/**
 * MyQA
 *
 * MyQA block
 *
 * @return {jsx}
 */
export const MyQA = ({
  handleReadMore,
  handleScheduleConsultationClick,
  questions,
  tabs,
  setTabs,
  isUserQuestionsEnabled,
  filterTag,
  setFilterTag,
  isQuestionsDataLoading,
  selectedLanguage,
  setSelectedLanguage,
  setShouldFetchQuestions,
  setIsHowItWorksOpen,
  searchValue,
  onResetSearch,
}) => {
  const { t } = useTranslation("blocks", { keyPrefix: "my-qa" });
  const navigate = useNavigate();
  const IS_RTL = localStorage.getItem("language") === "ar";
  const { allLanguages } = useContext(ThemeContext);
  const { data: languages } = useGetLanguages();
  const [tags, setTags] = useState([]);

  const onTagsSuccess = useCallback((data) => {
    setTags(data);
  }, []);

  useGetQuestionsTags({
    languageId: selectedLanguage,
    onSuccess: onTagsSuccess,
    enabled: true,
  });

  useEffect(() => {
    if (languages?.length) {
      setSelectedLanguage("all");
      setShouldFetchQuestions(true);
    }
  }, [languages, setSelectedLanguage, setShouldFetchQuestions]);

  const languageOptions = useMemo(() => {
    const showAllOption = {
      value: "all",
      label: t("all_languages"),
    };

    if (!languages) return [showAllOption];
    if (!languages.length) return [showAllOption, ...allLanguages];
    return [
      showAllOption,
      ...languages.map((x) => ({
        value: x.language_id,
        label: x.name === "English" ? x.name : `${x.name} (${x.local_name})`,
      })),
    ];
  }, [languages, allLanguages, t]);

  const handleTabChange = (index) => {
    const tabsCopy = [...tabs];

    for (let i = 0; i < tabsCopy.length; i++) {
      if (i === index) {
        tabsCopy[i].isSelected = true;
      } else {
        tabsCopy[i].isSelected = false;
      }
    }
    setTabs(tabsCopy);
  };

  const getTagsOptions = useMemo(() => {
    return tags.map((tag) => ({
      label: tag.label,
      value: tag.id,
      isSelected: filterTag === tag.label,
    }));
  }, [tags, filterTag]);

  const handleTagSelect = (index) => {
    const selectedTag = tags[index];
    if (selectedTag && setFilterTag) {
      if (filterTag === selectedTag.label) {
        setFilterTag("");
      } else {
        setFilterTag(selectedTag.label);
      }
    }
  };

  const handleProviderClick = (providerId) => {
    if (!providerId) return;
    navigate(`/provider-overview?id=${providerId}`);
  };

  const handleResetAllFilters = () => {
    onResetSearch?.();
    setFilterTag?.("");
    if (tabs?.length) {
      const resetTabs = tabs.map((tab, i) => ({
        ...tab,
        isSelected: i === 0,
      }));
      setTabs(resetTabs);
    }
    setSelectedLanguage?.("all");
  };

  const handleClearSearchAndBrowse = () => {
    handleResetAllFilters();
  };

  const renderEmptyCard = (headingText) => (
    <GridItem md={8} lg={12}>
      <div className="my-qa__empty-card">
        <NotFoundCard
          mode="illustrated"
          headingText={headingText}
          descriptionLine1={t("no_results_line1")}
          descriptionLine2={t("no_results_line2")}
          primaryLabel={t("reset_filters")}
          secondaryLabel={t("browse_all_questions")}
          onPrimaryClick={handleResetAllFilters}
          onSecondaryClick={handleClearSearchAndBrowse}
          imageAlt={t("no_results_image_alt")}
          isRtl={IS_RTL}
          radialColor="blue"
        />
      </div>
    </GridItem>
  );

  const renderQuestions = () => {
    const filteredQuestions = questions.filter((question) => {
      if (filterTag) {
        const tags = question.tags;
        if (!tags.includes(filterTag)) {
          return null;
        }
      }
      const value = searchValue.toLowerCase();

      if (value) {
        const isTitleMatching = question.answerTitle
          ?.toLowerCase()
          .includes(value);
        const isTextMatching = question.answerText
          ?.toLowerCase()
          .includes(value);
        const isTagMatching = question.tags?.find((x) =>
          x.toLowerCase().includes(value),
        );
        const isQuestionMatching = question.question
          ?.toLowerCase()
          .includes(value);

        const isMatching =
          isTitleMatching ||
          isTextMatching ||
          isTagMatching ||
          isQuestionMatching
            ? true
            : false;
        return !!isMatching;
      }

      return true;
    });

    if (!filteredQuestions.length) {
      const headingText =
        searchValue?.trim()?.length > 0
          ? t("no_results_heading", { query: searchValue.trim() })
          : t("no_answers");
      return renderEmptyCard(headingText);
    }

    return filteredQuestions.map((question, index) => {
      return (
        <GridItem key={index} md={8} lg={6}>
          <Answer
            question={question}
            classes="my-qa__answer"
            isInYourQuestions={isUserQuestionsEnabled}
            handleReadMore={() => handleReadMore(question)}
            handleScheduleConsultationClick={handleScheduleConsultationClick}
            handleProviderClick={handleProviderClick}
            t={t}
            renderIn="website"
          />
        </GridItem>
      );
    });
  };

  return (
    <Block classes="my-qa" data-clarity-mask="true">
      <div>
        <div className="my-qa__header">
          <div
            className="page__header__text-container__go-back"
            onClick={() => navigate(-1)}
          >
            <Icon name="arrow-chevron-back" size="md" color="#20809E" />
            <p>{t("go_back")}</p>
          </div>
          <NewButton
            size="lg"
            label={t("how_it_works")}
            onClick={setIsHowItWorksOpen}
            classes="my-qa__header__button"
          />
        </div>
      </div>
      <Grid>
        <GridItem xs={4} md={8} lg={12} classes="my-qa__controls-item">
          <Grid classes="my-qa__tabs-grid">
            <GridItem
              md={8}
              lg={12}
              classes="my-qa__tabs-grid__search-container"
            >
              <Grid>
                <GridItem md={4} lg={6}>
                  <div className="my-qa__tabs-grid__search-container__tabs">
                    <TabsUnderlined
                      textType="h3"
                      options={tabs.map((tab) => {
                        return {
                          label: t(tab.value),
                          value: tab.value,
                          isSelected: tab.isSelected,
                        };
                      })}
                      handleSelect={handleTabChange}
                    />
                  </div>
                </GridItem>
                <GridItem md={4} lg={6}>
                  <div className="my-qa__tabs-grid__search-container__dropdown">
                    <Dropdown
                      options={languageOptions}
                      selected={selectedLanguage}
                      setSelected={(lang) => {
                        setSelectedLanguage(lang);
                        setFilterTag("");
                      }}
                      placeholder={t("language")}
                    />
                  </div>
                </GridItem>
              </Grid>
            </GridItem>
            {tags.length > 0 && (
              <GridItem
                md={8}
                lg={12}
                classes="my-qa__tabs-grid__tags-container"
              >
                <Tabs
                  options={getTagsOptions}
                  handleSelect={handleTagSelect}
                  t={t}
                />
              </GridItem>
            )}
            <GridItem md={8} lg={12} classes="my-qa__button-item">
              <NewButton
                label={t("ask_button_label")}
                size="lg"
                classes="my-qa__button-item__button"
                isFullWidth={true}
                onClick={() => handleScheduleConsultationClick("question")}
              />
            </GridItem>
          </Grid>
        </GridItem>
        <GridItem xs={4} md={8} lg={12} classes="my-qa__answers-item">
          {questions?.length > 0 ? (
            <Grid classes="my-qa__answers-container">{renderQuestions()}</Grid>
          ) : isQuestionsDataLoading ? (
            <Loading />
          ) : (
            <Grid classes="my-qa__answers-container">
              {renderEmptyCard(t("no_answers"))}
            </Grid>
          )}
        </GridItem>
      </Grid>
    </Block>
  );
};
